import { useState, useEffect } from "react";
import { CaretDown, Check } from "@phosphor-icons/react";

const PHASES = [
  { key: "FOCUS", index: 1, sub: "Plan", accent: "blue" },
  { key: "ARMOR", index: 2, sub: "Execute", accent: "amber" },
];

const ACCENT = {
  blue: {
    text: "text-blue-700",
    headBg: "bg-blue-50",
    badge: "bg-blue-100 text-blue-700",
    activeBorder: "border-blue-600",
    activeText: "text-blue-700",
    dot: "bg-blue-600",
  },
  amber: {
    text: "text-amber-700",
    headBg: "bg-amber-50",
    badge: "bg-amber-100 text-amber-700",
    activeBorder: "border-amber-600",
    activeText: "text-amber-700",
    dot: "bg-amber-600",
  },
};

export default function PhaseStepper({ steps, currentStep, onSelect }) {
  const currentPhase = steps[currentStep - 1]?.phase || "FOCUS";
  const [expanded, setExpanded] = useState(currentPhase);

  // When the wizard moves into a new phase, expand that phase (and collapse the other)
  useEffect(() => {
    setExpanded(currentPhase);
  }, [currentPhase]);

  return (
    <div className="mt-6 mb-6 space-y-2" data-testid="phase-stepper">
      {PHASES.map((phase) => {
        const a = ACCENT[phase.accent];
        const phaseSteps = steps.filter((s) => s.phase === phase.key);
        const total = phaseSteps.length;
        const doneCount = phaseSteps.filter((s) => s.n < currentStep).length;
        const isCurrent = phase.key === currentPhase;
        const isExpanded = expanded === phase.key;
        const complete = doneCount === total && !isCurrent;

        return (
          <div
            key={phase.key}
            className={`border rounded-sm overflow-hidden transition-colors ${
              isCurrent ? "border-slate-300" : "border-slate-200"
            }`}
          >
            {/* Phase header */}
            <button
              type="button"
              data-testid={`phase-header-${phase.key.toLowerCase()}`}
              onClick={() => setExpanded(isExpanded ? null : phase.key)}
              className={`w-full flex items-center justify-between px-4 py-2.5 transition-colors ${
                isCurrent ? a.headBg : "bg-white hover:bg-slate-50"
              }`}
            >
              <div className="flex items-center gap-3 min-w-0">
                <span className={`font-mono text-xs font-bold px-2 py-1 rounded-sm shrink-0 ${a.badge}`}>
                  PHASE {phase.index} · {phase.key}
                </span>
                <span className="font-mono text-[11px] text-slate-400 uppercase tracking-wider hidden sm:inline">
                  ({phase.sub})
                </span>
                {/* Collapsed: compact letter dots */}
                {!isExpanded && (
                  <div className="flex items-center gap-1.5">
                    {phaseSteps.map((s) => {
                      const state = s.n === currentStep ? "active" : s.n < currentStep ? "done" : "pending";
                      return (
                        <span
                          key={s.n}
                          title={`${s.code} — ${s.label}`}
                          className={`w-5 h-5 rounded-sm flex items-center justify-center font-mono text-[10px] font-bold transition-colors ${
                            state === "active"
                              ? `${a.dot} text-white`
                              : state === "done"
                              ? "bg-emerald-500 text-white"
                              : "bg-slate-200 text-slate-500"
                          }`}
                        >
                          {s.code}
                        </span>
                      );
                    })}
                  </div>
                )}
              </div>

              <div className="flex items-center gap-3 shrink-0">
                <span className="font-mono text-[11px] text-slate-500">
                  {complete ? (
                    <span className="inline-flex items-center gap-1 text-emerald-600">
                      <Check size={13} weight="bold" /> Done
                    </span>
                  ) : (
                    `${doneCount}/${total}`
                  )}
                </span>
                <CaretDown
                  size={16}
                  className={`text-slate-400 transition-transform duration-200 ${isExpanded ? "rotate-180" : ""}`}
                />
              </div>
            </button>

            {/* Expanded: full step chips */}
            <div
              className={`grid transition-all duration-300 ease-in-out ${
                isExpanded ? "grid-rows-[1fr] opacity-100" : "grid-rows-[0fr] opacity-0"
              }`}
            >
              <div className="overflow-hidden">
                <div className="grid grid-cols-5 gap-1.5 px-3 pb-3 pt-1 border-t border-slate-100">
                  {phaseSteps.map((s) => {
                    const state = s.n === currentStep ? "active" : s.n < currentStep ? "done" : "pending";
                    return (
                      <button
                        key={s.n}
                        type="button"
                        data-testid={`stepper-${s.n}`}
                        onClick={() => onSelect(s.n)}
                        className={`flex flex-col text-left border-t-4 pt-2 px-1.5 pb-1 rounded-b-sm transition-all hover:bg-slate-50 ${
                          state === "active"
                            ? a.activeBorder
                            : state === "done"
                            ? "border-emerald-500"
                            : "border-slate-200 opacity-70 hover:opacity-100"
                        }`}
                      >
                        <span
                          className={`font-mono text-[10px] font-bold tracking-widest uppercase ${
                            state === "active" ? a.activeText : state === "done" ? "text-emerald-600" : "text-slate-400"
                          }`}
                        >
                          {s.n}. {s.code}
                        </span>
                        <span className="text-xs sm:text-sm font-medium text-slate-800 leading-tight">{s.label}</span>
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}
